import "./additional-details.css";

import { Input } from "@tql/ui-components-react/kendo/inputs";
import { Label } from "@tql/ui-components-react/kendo/labels";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { useCallback, useContext, useRef } from "react";

import { AccessorialServicesList } from "~/features/quote/components/quote-details/components/additional-details/accessorial-services-list.tsx";
import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import { useSubmitQuote } from "~/features/quote/hooks/use-quote-details-hooks.ts";
import { useAutoSpinner } from "~/shared/hooks/use-global-spinner.ts";

type AdditionalDetailsProps = {
	onError: (message: string) => void;
	onStepChange: () => void;
	onSubmit?: () => Promise<{ success: boolean; error?: string }>;
	onBeforeSubmit?: () => boolean;
	onSaveTemplate?: () => void;
	submitButtonText?: string;
	isSubmitting?: boolean;
	showSubmitButton?: boolean;
	showSaveTemplateButton?: boolean;
	disableSaveTemplateButton?: boolean;
	disableSubmitButton?: boolean;
};

export const AdditionalDetails = ({
	onError,
	onSubmit,
	onBeforeSubmit,
	onSaveTemplate,
	submitButtonText,
	isSubmitting,
	showSubmitButton = true,
	showSaveTemplateButton = false,
	disableSaveTemplateButton = false,
	disableSubmitButton = false,
}: AdditionalDetailsProps) => {
	const { quoteState, onChange } = useContext(QuoteDetailsContext);
	const { submitQuote, isLoading: isQuoteLoading } = useSubmitQuote();

	// Callers that supply their own onSubmit also own the pending flag; otherwise
	// fall back to the pending state of the default submitQuote mutation.
	const isBusy = isSubmitting ?? isQuoteLoading;

	// Automatically manage global spinner for quote submission
	useAutoSpinner(isBusy);

	// Blocks duplicate submits fired before React commits the pending state,
	// e.g. a held Enter key repeating on the focused button.
	const inFlightRef = useRef(false);

	const handleSubmit = useCallback(async () => {
		if (inFlightRef.current) {
			return;
		}
		if (onBeforeSubmit && !onBeforeSubmit()) {
			return;
		}
		inFlightRef.current = true;
		try {
			const action = onSubmit ?? submitQuote;
			const result = await action();
			if (!result.success) {
				const errorMsg =
					result.error || "An error occurred while submitting the quote";
				onError(errorMsg);
			}
		} finally {
			inFlightRef.current = false;
		}
	}, [submitQuote, onSubmit, onBeforeSubmit, onError]);

	// const getQuoteDisabled = () => {
	// 	const hasAvailableCreditProperty =
	// 		quoteState.customer?.id &&
	// 		quoteState.customerCredits &&
	// 		"availableCredit" in quoteState.customerCredits;

	// 	return !hasAvailableCreditProperty;
	// };

	return (
		<>
			<Label className={"additional-details-section-title"}>
				Accessorial Services
			</Label>
			<div className={"additional-details-container"}>
				<AccessorialServicesList />

				<div className={"customer-references-wrapper"}>
					<Label
						className={"k-form-label additional-details-section-title"}
						id={"customerReference-label"}
					>
						Customer References
					</Label>
					<div className={"customer-references-input"}>
						<Input
							id={"customerReference"}
							name={`customerReference`}
							onChange={({ value }: { value: string }) => {
								onChange("customerReference", value);
							}}
							tabIndex={-1}
							value={quoteState.customerReference || ""}
						/>
					</div>
				</div>

				{showSubmitButton ? (
					<div className={"k-form-buttons"}>
						{showSaveTemplateButton ? (
							<Button
								disabled={disableSaveTemplateButton}
								onClick={onSaveTemplate}
								tabIndex={-1}
								variant={"tertiary"}
							>
								Save as Template
							</Button>
						) : null}
						<Button
							disabled={disableSubmitButton}
							// disabled={getQuoteDisabled()}
							formAction={"submit"}
							loading={isBusy}
							onClick={() => {
								void handleSubmit();
							}}
							onKeyDown={(e: React.KeyboardEvent) => {
								if (e.key === "Enter" || e.key === " ") {
									e.preventDefault();
									if (e.repeat || isBusy) return;
									void handleSubmit();
								}
							}}
							variant={"primary"}
						>
							{submitButtonText ?? "Get Quote"}
						</Button>
					</div>
				) : null}
			</div>
		</>
	);
};
